import TextConvo from '@/components/sms/text-convo/text-convo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { PDKAIIcon, PDKAlertTriangleIcon } from '@/components/ui/pdk-icons';
import { RadioCardsItem, RadioCardsRoot } from '@/components/ui/radio-cards';
import { Textarea } from '@/components/ui/textarea';
import { usePostHog } from '@/contexts/posthog-context';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { POSTHOG_EVENTS } from '@/lib/posthog-events';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export type AISummary = {
  bullet: string;
  date_time: string;
  subject: string;
}[];

const TextRecap = ({
  aiSummary,
  dealShortId,
  smsConversation,
}: {
  aiSummary: AISummary | null;
  dealShortId: string;
  smsConversation?: string[] | null;
}) => {
  const { isFeatureEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.TEXT_RECAP,
  });

  const { isFeatureEnabled: isTextConvoEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.TEXT_CONVO,
  });
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackHelpful, setFeedbackHelpful] = useState<string>('');
  const [feedbackText, setFeedbackText] = useState('');
  const posthog = usePostHog();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const feedbackMutation = useMutation({
    mutationFn: async (data: {
      was_helpful: boolean;
      feedback: string;
      deal_short_id: string;
      ai_summary: AISummary;
    }) => {
      const response = await pdkAxios.post('/feedback/text-recap', data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Feedback submitted',
        description: 'Thank you for your feedback!',
      });
      setFeedbackDialogOpen(false);
      setFeedbackHelpful('');
      setFeedbackText('');
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to submit feedback. Please try again.',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (isFeatureEnabled && buttonRef.current) {
      posthog?.capture(POSTHOG_EVENTS.TEXT_RECAP.BUTTON_SHOWN, {
        deal_short_id: dealShortId,
      });
    }
  }, [isFeatureEnabled, dealShortId, aiSummary, posthog, buttonRef.current]);

  const handleTextRecapClick = () => {
    posthog?.capture(POSTHOG_EVENTS.TEXT_RECAP.BUTTON_CLICKED, {
      deal_short_id: dealShortId,
    });
  };

  if (!isFeatureEnabled || isEmpty(aiSummary)) {
    return null;
  }

  return (
    <Dialog modal={false}>
      <DialogTitle className="sr-only">Text Recap</DialogTitle>
      <DialogTrigger asChild>
        <DialogHeader>
          <Button
            ref={buttonRef}
            variant="outline"
            size="sm"
            onClick={handleTextRecapClick}
          >
            <PDKAIIcon />
            Text Recap
          </Button>
        </DialogHeader>
      </DialogTrigger>

      <DialogContent
        className="fixed left-0 top-0 h-screen max-w-[96vw] sm:max-w-96 translate-x-0 translate-y-0 rounded-none border-r flex flex-col gap-10"
        onClickClose={() => {}}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogDescription className="sr-only">
          Text Recap is a tool that helps you summarize text messages.
        </DialogDescription>
        <div className="text-sm  flex flex-row items-center gap-2 h-fit">
          <PDKAIIcon className="text-teal-400" />
          Text Recap
        </div>
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20 p-3 h-fit">
          <div className="flex gap-2">
            <PDKAlertTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-xs text-amber-900 dark:text-amber-200 space-y-2">
              <p>AI can hallucinate information</p>
              <p className="text-amber-800 dark:text-amber-300">
                We haven't yet trained the AI to not hallucinate. Please verify
                information with teammates
                {isTextConvoEnabled && <>or please check the text convo</>}.
              </p>
              {smsConversation && (
                <div className="pt-1">
                  <TextConvo
                    smsConversation={smsConversation}
                    dealShortId={dealShortId}
                    isFromTextRecap
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
        <div className="flex flex-col gap-2 text-xs overflow-y-auto">
          {aiSummary &&
            aiSummary.map((item, index) => {
              return (
                <div key={index}>
                  <div className="p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-gray-900 dark:text-gray-100 text-xs flex items-center gap-1">
                        <PDKAIIcon className="text-teal-400" />
                        {item.subject}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">
                        {dayjs
                          .utc(item.date_time)
                          .local()
                          .format('MMM D, h:mm A')}
                      </div>
                    </div>
                    <div className="text-gray-700 dark:text-gray-300 leading-relaxed transition-all duration-200 group-hover:">
                      {item.bullet}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <div className="absolute bottom-4 right-4">
          <Dialog
            open={feedbackDialogOpen}
            onOpenChange={setFeedbackDialogOpen}
          >
            <DialogTitle className="sr-only">Text Recap Feedback</DialogTitle>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="shadow-lg">
                <MessageCircle className="h-4 w-4 mr-1" />
                Give feedback
              </Button>
            </DialogTrigger>
            <DialogContent
              className="sm:max-w-md"
              onClickClose={() => setFeedbackDialogOpen(false)}
            >
              <DialogDescription className="sr-only">
                Text Recap is a tool that helps you summarize text messages.
              </DialogDescription>
              <DialogTitle className="sr-only">Text Recap Feedback</DialogTitle>
              <DialogHeader className="text-left">
                <h3 className="text-lg">Text Recap Feedback</h3>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <p className="text-sm">Was the Text Recap helpful?</p>
                  <RadioCardsRoot
                    value={feedbackHelpful}
                    onValueChange={setFeedbackHelpful}
                    className="flex gap-2"
                  >
                    <RadioCardsItem
                      value="yes"
                      className="flex items-center space-x-2 rounded-md border px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-50 dark:data-[state=checked]:bg-teal-950"
                    >
                      <span className="text-sm">Yes</span>
                    </RadioCardsItem>
                    <RadioCardsItem
                      value="no"
                      className="flex items-center space-x-2 rounded-md border px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-50 dark:data-[state=checked]:bg-teal-950"
                    >
                      <span className="text-sm">No</span>
                    </RadioCardsItem>
                  </RadioCardsRoot>
                </div>

                <div className="space-y-2">
                  <p className="text-sm">Give us any feedback</p>
                  <p className="text-xs text-gray-500">
                    All feedback will be read by a human.
                  </p>
                  <p className="text-xs text-gray-500">
                    What would you like the summary to focus on? Was anything
                    inaccurate?
                  </p>
                  <Textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Share your feedback..."
                    className="min-h-[100px] border border-gray-300 dark:border-gray-700"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFeedbackDialogOpen(false);
                      setFeedbackHelpful('');
                      setFeedbackText('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!feedbackHelpful) {
                        toast({
                          title: 'Please select an option',
                          description: 'Was the Text Recap helpful?',
                          variant: 'destructive',
                        });
                        return;
                      }

                      if (!aiSummary) {
                        toast({
                          title: 'No summary to submit feedback for',
                          description:
                            "Can only submit feedback if there's a summary.",
                          variant: 'destructive',
                        });
                        return;
                      }

                      feedbackMutation.mutate({
                        deal_short_id: dealShortId,
                        was_helpful: feedbackHelpful === 'yes',
                        feedback: feedbackText,
                        ai_summary: aiSummary || [],
                      });
                    }}
                    disabled={feedbackMutation.isPending}
                  >
                    {feedbackMutation.isPending
                      ? 'Submitting...'
                      : 'Submit Feedback'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TextRecap;
